import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPORT_REPORTS_ROUTE } from "../../../../../../constants/routes"
import React, { useMemo, useRef, useState } from "react"
import { MRT_ColumnDef } from "material-react-table"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { Stack } from "@mui/system"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useDateTime } from "../../../../../localization/useDateTime"
import { DateTime } from "luxon"
import { fetchSupportMessagesEmails, SupportEmailsResponse, SupportReportsEmailQuery } from "../emailApi"
import { SupportEmail } from "../emailTypes"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { Button } from "../../../../../office/admin/components/Button"
import { DetailedPanel } from "../components/DetailPanel"
import { ReceiverInput } from "../components/ReceiverInput"
import { EmailSubjectInput } from "../components/EmailSubjectInput"
import { selectedEmailsToApi } from "../helpers/selectedEmailsToApi"
import { ConfirmModal } from "../../../../../../shared/components/dialogs/ConfirmModal"
import { useResendEmail } from "../hooks/useResendEmail"
import { useExportEmails } from "../hooks/useExportEmails"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { type DtDateRange, StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

function transformData(data: SupportEmailsResponse) {
    return data?.emails || []
}

export function MessagesEmailList() {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const [email, setEmail] = useState("")
    const [subject, setSubject] = useState("")
    const tableInstanceRef = useRef<MRT_TableInstance<SupportEmail>>(null)

    const { startDate, endDate } = useInitialDateFilters(DateTime.now().minus({ days: 14 }))
    const [dateState, setDateState] = useState<DtDateRange>({
        startDate: startDate,
        endDate: endDate,
    })

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})

    const [showModal, setShowModal] = useState(false)

    const supportEmails = useApiQuery(fetchSupportMessagesEmails, {
        queryName: SupportReportsEmailQuery.EMAILS_FOR_SUPPORT,
        dependencies: {
            subject: subject,
            email: email,
            from: dateState.startDate.toISODate(),
            to: dateState.endDate.toISODate(),
        },
        select: transformData,
    })

    const { mutate } = useResendEmail()

    const { ApiQueryTable, columnFactory } = useApiQueryTable(supportEmails)

    const onResendClick = () => {
        const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key])

        const emails = selectedEmailsToApi(
            supportEmails?.data?.filter((email) => selectedIds.includes(email.id.toString())),
        )

        if (!emails) return

        mutate({ emails })
    }

    const { handleExportRows } = useExportEmails(dateState.startDate.toISODate(), dateState.endDate.toISODate())

    const exportSelectedRows = () => {
        handleExportRows(tableInstanceRef.current?.getSelectedRowModel().rows)
    }

    const columns = useMemo<MRT_ColumnDef<SupportEmail>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("support-messages-email-table-header-receiver"),
                accessorKey: "receiver.to",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("support-messages-email-table-header-subject"),
                accessorKey: "body",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={row.original.subject}
                        subtitle1={
                            <LocalizedStrict
                                id="support-messages-email-table-cell-subject-created"
                                vars={{
                                    time: dateTimeFactory.formatDateTime(
                                        dateTimeFactory.fromApi(row.original.history.created),
                                    ),
                                }}
                            >
                                <>
                                    Created:{" "}
                                    {dateTimeFactory.formatDateTime(
                                        dateTimeFactory.fromApi(row.original.history.created),
                                    )}
                                </>
                            </LocalizedStrict>
                        }
                    />
                ),
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("support-messages-email-table-header-sent-time"),
                    accessorKey: "sentTime",
                },
                (row) => row.sentTime,
            ),
            columnFactory.statusColumn({
                header: l10n.getString("support-messages-email-table-header-status"),
                accessorKey: "status.text",
                Cell: ({ row }) => {
                    return row.original.status.text === "Sent" ? (
                        <LocalizedStrict id="support-messages-email-table-status-sent">
                            <StandardChip variant="success" placement="table">
                                Sent
                            </StandardChip>
                        </LocalizedStrict>
                    ) : (
                        <LocalizedStrict id="support-messages-email-table-status-not-sent">
                            <StandardChip variant="red" placement="table">
                                Not sent
                            </StandardChip>
                        </LocalizedStrict>
                    )
                },
                filterSelectOptions: [
                    {
                        value: "sent",
                        text: l10n.getString("support-messages-email-table-status-sent"),
                    },
                    {
                        value: "not sent",
                        text: l10n.getString("support-messages-email-table-status-not-sent"),
                    },
                ],
            }),
        ],
        [l10n, columnFactory, dateTimeFactory],
    )

    return (
        <>
            <LocalizedStrict id="support-messages-email-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_MESSAGES_EMAIL}
                    title="Email"
                    rightElement={
                        <Stack direction="row" spacing="1.5rem">
                            <LocalizedStrict id="support-messages-email-resend-button">
                                <Button
                                    disabled={Object.keys(rowSelection).length <= 0}
                                    onClick={() => setShowModal(true)}
                                >
                                    Resend
                                </Button>
                            </LocalizedStrict>
                            <LocalizedStrict id="support-messages-email-export-button">
                                <Button disabled={Object.keys(rowSelection).length <= 0} onClick={exportSelectedRows}>
                                    Export
                                </Button>
                            </LocalizedStrict>
                        </Stack>
                    }
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-messages">
                            <Breadcrumb>Messages</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-email">
                            <Breadcrumb>Email</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                enableExpandAll={false}
                enableExpanding
                columns={columns}
                onRowSelectionChange={setRowSelection}
                state={{
                    rowSelection,
                }}
                getRowId={(originalRow, index) => {
                    if (!originalRow.id) return index.toString()

                    return originalRow.id.toString()
                }}
                initialState={{ density: "spacious" }}
                enableRowSelection
                renderDetailPanel={({ row }) => <DetailedPanel data={row.original} />}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                        <ReceiverInput value={email} onChange={setEmail} />
                        <EmailSubjectInput value={subject} onChange={setSubject} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
            />

            {showModal && (
                <ConfirmModal
                    open={showModal}
                    onClose={() => setShowModal(false)}
                    title={
                        <LocalizedStrict id="support-messages-email-resent-dialog-title">
                            <>Confirm to resend emails</>
                        </LocalizedStrict>
                    }
                    message={
                        <LocalizedStrict
                            id="support-messages-email-resent-dialog-subtitle"
                            vars={{
                                itemsAmount: Object.keys(rowSelection).length,
                            }}
                        >
                            <>{`Are you sure you want to resend ${Object.keys(rowSelection).length} emails?`}</>
                        </LocalizedStrict>
                    }
                    handleClick={onResendClick}
                    buttonLabelCancel={l10n.getString("support-messages-email-resent-dialog-cancel-button")}
                    buttonLabelSubmit={l10n.getString("support-messages-email-resent-dialog-submit-button")}
                />
            )}
        </>
    )
}
